import type { AgreementAudience, AgreementRequiredAction } from '@/api/schemas/agreements.types';
import type { SigningVerificationPolicy } from '@/api/schemas/agreements.types';

/**
 * The authoring draft.
 *
 * ONE object holds every step's values, because the steps are not independent:
 * the requirement step's shape depends on the audience and action chosen in
 * step 1, and the review step reads all three. Splitting the draft per step
 * would mean lifting it back into one place to answer those questions anyway.
 *
 * The draft is NOT the wire shape. Three separate API calls consume it —
 * `createDocument`, `publishVersion`, `createRequirement` — and each takes only
 * the fields it accepts. Keeping the draft flat and mapping at the call site is
 * what stops a step from accidentally sending a field the API would refuse.
 */
export type DocumentDraft = {
  /* Step 1 — the document's stable identity. */
  documentKey: string;
  title: string;
  audience: AgreementAudience;
  requiredAction: AgreementRequiredAction;

  /* Step 2 — the first published version. */
  locale: string;
  isDefaultLocale: boolean;
  versionTitle: string;
  content: string;
  effectiveAt: string;

  /* Step 3 — the requirement that makes the document binding. */
  requirementAction: AgreementRequiredAction;
  verificationPolicy: SigningVerificationPolicy | '';
  reacceptanceRequired: boolean;
  deadlineAt: string;
};

/**
 * What the flow has already created on the server.
 *
 * Recorded as each call succeeds, because the calls are NOT atomic: a document
 * can exist with no version, and a version can exist with no requirement. When a
 * later step fails the operator must be told exactly how far the work got, and
 * that is only knowable from what actually returned.
 */
export type DocumentProgress = {
  documentId: string | null;
  versionId: string | null;
  requirementId: string | null;
};

/** The empty draft. A function, not a constant, so two mounts never share one object. */
export function emptyDraft(): DocumentDraft {
  return {
    documentKey: '',
    title: '',
    audience: 'tenant',
    requiredAction: 'signature',
    locale: 'en',
    isDefaultLocale: true,
    versionTitle: '',
    content: '',
    effectiveAt: new Date().toISOString().slice(0, 10),
    requirementAction: 'signature',
    verificationPolicy: '',
    reacceptanceRequired: true,
    deadlineAt: '',
  };
}

/** The empty progress record. */
export function emptyProgress(): DocumentProgress {
  return { documentId: null, versionId: null, requirementId: null };
}

/**
 * Whether the draft still holds nothing the user typed.
 *
 * Compares against `emptyDraft()` field by field rather than tracking a dirty
 * flag. A flag has to be set by every field and cleared by every reset, and the
 * one field that forgets to set it is the one that silently loses the user's
 * work. This cannot drift, because there is nothing to keep in sync.
 *
 * `effectiveAt` is excluded: it is pre-filled with today's date, so including it
 * would make every untouched form look dirty and train the user to click through
 * the discard confirmation without reading it.
 */
export function isDraftEmpty(draft: DocumentDraft): boolean {
  const empty = emptyDraft();
  return (Object.keys(empty) as ReadonlyArray<keyof DocumentDraft>).every(
    (key) => key === 'effectiveAt' || draft[key] === empty[key],
  );
}
